import React, { useMemo, useRef } from 'react';
import { BookOpen, Check, Search } from 'lucide-react';
import { Badge, Card, CardContent, Input } from '@ncoder/ui';
import { PageHeader } from '@/components/molecules/PageHeader';
import { OnThisPageNav, type TocSection } from '@/components/molecules/docs/OnThisPageNav';
import { useSpringScrollTo } from '@/components/molecules/docs/useSpringScrollTo';
import { useTocSearch } from '@/components/molecules/docs/useTocSearch';
import { Callout } from '@/components/molecules/docs/Callout';
import { CodeSnippet } from '@/components/molecules/docs/CodeSnippet';
import { InlineText } from '@/components/molecules/docs/InlineText';
import { userGuide, type GuideProcedure, type GuideCategory, type GuideImage, type GuideSubsection } from './data';

function slugify(title: string) {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-+|-+$)/g, '');
}

function buildToc(categories: GuideCategory[]): TocSection[] {
  return categories.map((cat) => ({
    id: slugify(cat.title),
    label: `${cat.number}. ${cat.title}`,
    groups: [{ items: cat.procedures.map((p) => ({ id: slugify(p.title), title: p.title })) }],
  }));
}

function MetaItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/70">{label}</div>
      <div className="mt-0.5 text-foreground">{value}</div>
    </div>
  );
}

function GuideScreenshot({ image }: { image: GuideImage }) {
  return (
    <div
      className="overflow-hidden rounded-lg border border-border bg-muted/30"
      style={image.aspect ? { aspectRatio: image.aspect } : undefined}
    >
      <img src={image.src} alt={image.alt} loading="lazy" className="block h-full w-full object-cover" />
    </div>
  );
}

function ProcedureSubsection({ subsection }: { subsection: GuideSubsection }) {
  return (
    <div className="space-y-3 border-t border-border pt-4">
      <h4 className="text-sm font-medium text-foreground">{subsection.title}</h4>
      {subsection.lede && (
        <p className="text-sm text-muted-foreground">
          <InlineText text={subsection.lede} />
        </p>
      )}
      {subsection.checklist && (
        <ul className="grid gap-2 sm:grid-cols-2">
          {subsection.checklist.map((item, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
              <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
              {item}
            </li>
          ))}
        </ul>
      )}
      {subsection.image && <GuideScreenshot image={subsection.image} />}
      {subsection.calloutsAfter?.map((c, i) => (
        <Callout key={i} type={c.type} label={c.label}>
          <InlineText text={c.text} />
        </Callout>
      ))}
    </div>
  );
}

function ProcedureCard({ procedure, index, id }: { procedure: GuideProcedure; index: number; id: string }) {
  const hasMeta = procedure.timeRequired || procedure.difficulty || procedure.prerequisites;

  return (
    <Card id={id} className="scroll-mt-4">
      <CardContent className="flex items-start gap-4 p-5">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
          {index + 1}
        </div>
        <div className="min-w-0 flex-1 space-y-3">
          <h3 className="text-base font-medium text-foreground">{procedure.title}</h3>

          {hasMeta && (
            <div className="flex flex-wrap gap-x-6 gap-y-2 border-y border-border py-3 text-xs">
              {procedure.timeRequired && <MetaItem label="Time required" value={procedure.timeRequired} />}
              {procedure.difficulty && <MetaItem label="Difficulty" value={procedure.difficulty} />}
              {procedure.prerequisites && <MetaItem label="Prerequisites" value={procedure.prerequisites} />}
            </div>
          )}

          {procedure.lede && (
            <p className="text-sm text-muted-foreground">
              <InlineText text={procedure.lede} />
            </p>
          )}

          {procedure.calloutsBefore?.map((c, i) => (
            <Callout key={i} type={c.type} label={c.label}>
              <InlineText text={c.text} />
            </Callout>
          ))}

          {procedure.steps && (
            <ol className="space-y-3">
              {procedure.steps.map((step, si) => (
                <li key={si} className="flex gap-3 text-sm">
                  <span className="mt-0.5 shrink-0 font-mono text-xs text-muted-foreground">{si + 1}.</span>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-foreground">{step.title}</div>
                    {step.body && (
                      <p className="mt-0.5 text-muted-foreground">
                        <InlineText text={step.body} />
                      </p>
                    )}
                    {step.code && <CodeSnippet code={step.code} />}
                  </div>
                </li>
              ))}
            </ol>
          )}

          {procedure.image && <GuideScreenshot image={procedure.image} />}

          {procedure.calloutsAfter?.map((c, i) => (
            <Callout key={i} type={c.type} label={c.label}>
              <InlineText text={c.text} />
            </Callout>
          ))}

          {procedure.subsections?.map((sub, i) => <ProcedureSubsection key={i} subsection={sub} />)}
        </div>
      </CardContent>
    </Card>
  );
}

export const UserGuide: React.FC = () => {
  const toc = useMemo(() => buildToc(userGuide.categories), []);
  const scrollRef = useRef<HTMLDivElement>(null);
  const scrollToId = useSpringScrollTo(scrollRef);
  const { query, setQuery, filteredSections } = useTocSearch(toc);

  return (
    <div className="flex h-full flex-col gap-4 animate-fade-in bg-background">
      <PageHeader
        icon={BookOpen}
        title="User Guide"
        description="Step-by-step procedures for using nCoder.ai yourself."
        actions={
          <div className="relative w-96">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search this guide..."
              className="h-8 pl-8 text-xs"
            />
          </div>
        }
      />

      <div className="flex min-h-0 flex-1 gap-6 overflow-hidden">
        <div ref={scrollRef} className="custom-scrollbar min-w-0 flex-1 overflow-y-auto pr-1">
          <div className="mx-auto max-w-2xl space-y-4 pb-8 text-center">
            <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">{userGuide.subtitle}</div>
            <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">{userGuide.title}</h1>
            <p className="text-muted-foreground">{userGuide.intro}</p>

            <div className="flex flex-wrap justify-center gap-2 pt-2">
              {userGuide.popularLinks.map((link) => (
                <button
                  key={link.procedureId}
                  type="button"
                  onClick={() => scrollToId(link.procedureId)}
                  className="rounded-full border border-border bg-muted/40 px-3 py-1.5 text-xs text-foreground transition-colors hover:border-primary/50 hover:text-primary"
                >
                  {link.label}
                </button>
              ))}
            </div>

            <div className="space-y-3 pt-2 text-left">
              {userGuide.homeCallouts.map((c, i) => (
                <Callout key={i} type={c.type} label={c.label}>
                  <InlineText text={c.text} />
                </Callout>
              ))}
            </div>
          </div>

          <div className="pb-2 pt-8">
            <h2 className="mb-3 text-lg font-semibold">How this guide is organized</h2>
            <div className="grid gap-3 sm:grid-cols-2">
              {userGuide.categories.map((cat) => (
                <button
                  key={cat.number}
                  type="button"
                  onClick={() => scrollToId(slugify(cat.title))}
                  className="rounded-lg border border-border p-4 text-left transition-colors hover:border-primary/50"
                >
                  <div className="text-sm font-medium text-foreground">
                    {cat.number}. {cat.title}
                  </div>
                  {cat.overviewLede && (
                    <div className="mt-1 text-xs text-muted-foreground">
                      <InlineText text={cat.overviewLede} />
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>

          {userGuide.categories.map((cat) => (
            <section key={cat.number} id={slugify(cat.title)} className="scroll-mt-4 space-y-4 pt-12">
              <div className="flex items-baseline gap-2 border-b border-border pb-3">
                <Badge variant="secondary" className="font-mono">
                  {String(cat.number).padStart(2, '0')}
                </Badge>
                <h2 className="text-xl font-semibold">{cat.title}</h2>
              </div>
              {cat.overviewLede && (
                <p className="text-sm text-muted-foreground">
                  <InlineText text={cat.overviewLede} />
                </p>
              )}
              {cat.overviewCallouts?.map((c, i) => (
                <Callout key={i} type={c.type} label={c.label}>
                  <InlineText text={c.text} />
                </Callout>
              ))}
              {cat.overviewImage && <GuideScreenshot image={cat.overviewImage} />}
              <div className="space-y-3">
                {cat.procedures.map((proc, index) => (
                  <ProcedureCard key={proc.title} procedure={proc} index={index} id={slugify(proc.title)} />
                ))}
              </div>
            </section>
          ))}
        </div>

        <OnThisPageNav sections={filteredSections} scrollContainerRef={scrollRef} />
      </div>
    </div>
  );
};

export default UserGuide;
